import React, {
  createContext, FC, memo, ReactNode, useCallback, useContext, useMemo, useState, Dispatch, SetStateAction,
} from 'react';
import lockPageScroll from '../../helpers/lockPageScroll';

export interface ModalsState {
  isCookiesModalOpen?: boolean;
  isCommunicationTermsModalOpen?: boolean;
  isQuickQuoteModalOpen?: boolean;
  isAreaPopupOpen?: boolean;
  isSignInModalOpen?: boolean;
}

interface ModalsContextValue {
  modalsState: ModalsState;
  properties: any;
  setProperties: Dispatch<SetStateAction<any>>; // ← add this
  maxProgressReached: number;
  setMaxProgressReached: Dispatch<SetStateAction<number>>;
  progressCtx: number;
  setProgressCtx: Dispatch<SetStateAction<number>>;
  isAdditionalFieldEnabled: boolean;
  setIsAdditionalFieldEnabled: Dispatch<SetStateAction<boolean>>;
}

interface ModalsProviderProps {
  initialState?: ModalsState;
  children: ReactNode;
}

type Toggle = (modalsStatePayload: ModalsState, modalProperties?: any) => void;

const ModalsContext = createContext<ModalsContextValue | null>(null);
const ToggleContext = createContext<Toggle | null>(null);

const ModalsProvider: FC<ModalsProviderProps> = ({ children, initialState }: ModalsProviderProps) => {
  let defaultState = {
    isCookiesModalOpen: false,
    isCommunicationTermsModalOpen: false,
    isQuickQuoteModalOpen: false,
    isAreaPopupOpen: false,
    isSignInModalOpen: false,
  };
  if (initialState) {
    defaultState = { ...defaultState, ...initialState };
  }
  const [modalsState, setModalsState] = useState(defaultState);

  const [properties, setProperties] = useState<any>({});

  const [progressCtx, setProgressCtx] = useState<number>(0);
  const [maxProgressReached, setMaxProgressReached] = useState<number>(0);

  const [isAdditionalFieldEnabled, setIsAdditionalFieldEnabled] = useState<boolean>(false);

  const checkIfScrollShouldBeLocked = (modifiedModalsState: ModalsState): boolean => {
    if (properties.isModalHidden) {
      return false;
    }

    let isSomeOpen = false;

    Object.keys(modifiedModalsState).forEach((key) => {
      if (modifiedModalsState[key as keyof ModalsState]) {
        isSomeOpen = true;
      }
    });

    return isSomeOpen;
  };

  const toggleModal = useCallback((modalsStatePayload: ModalsState, modalProperties?: any) => {
    if (modalProperties) {
      setProperties(modalProperties);
    }

    const modifiedModalsState = { ...modalsState, ...modalsStatePayload };
    setModalsState(modifiedModalsState);

    lockPageScroll(checkIfScrollShouldBeLocked(modifiedModalsState));
  }, [modalsState]);

  const value = useMemo(() => ({
    modalsState,
    properties,
    setProperties,
    maxProgressReached,
    setMaxProgressReached,
    progressCtx,
    setProgressCtx,
    isAdditionalFieldEnabled,
    setIsAdditionalFieldEnabled,
  }), [modalsState, properties, maxProgressReached, setMaxProgressReached, isAdditionalFieldEnabled, setIsAdditionalFieldEnabled,progressCtx]);

  return (
    <ModalsContext.Provider value={value}>
      <ToggleContext.Provider value={toggleModal}>
        {children}
      </ToggleContext.Provider>
    </ModalsContext.Provider>
  );
};
ModalsProvider.displayName = 'ModalsProvider';

export const useModalsContext = (): ModalsContextValue => {
  const context = useContext(ModalsContext);
  if (!context) {
    throw new Error('useModalsState must be used within a ModalsProvider');
  }

  return context;
};

export const useToggleModalsContext = (): Toggle => {
  const context = useContext(ToggleContext);
  if (!context) {
    throw new Error('useToggleModalsContext must be used within a ModalsProvider');
  }

  return context;
};

export default memo(ModalsProvider);
